import { useEffect, useState } from "react";
import { fetchFleetSummary, type FleetSummary } from "@/api/agent-control";

export function FleetDashboardTab() {
  const [summary, setSummary] = useState<FleetSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFleetSummary()
      .then(setSummary)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load fleet data"));
  }, []);

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400 m-4">
        {error}
      </div>
    );
  }

  if (!summary) {
    return <div className="p-4 text-sm text-muted-foreground">Loading fleet data...</div>;
  }

  return (
    <div className="space-y-6 p-4">
      <h3 className="text-lg font-semibold">Fleet Overview</h3>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total Agents" value={summary.total} />
        <StatCard
          label="Stale (>30d)"
          value={summary.stale_count}
          variant={summary.stale_count > 0 ? "warning" : "default"}
        />
        <StatCard
          label="Unreachable"
          value={summary.unreachable_count}
          variant={summary.unreachable_count > 0 ? "danger" : "default"}
        />
        <StatCard label="Versions" value={Object.keys(summary.by_version).length} />
      </div>

      {Object.keys(summary.by_version).length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Version Distribution</h4>
          <div className="space-y-1">
            {Object.entries(summary.by_version).map(([version, count]) => (
              <div key={version} className="flex items-center gap-2 text-sm">
                <span className="font-mono w-24 shrink-0">{version}</span>
                <div className="flex-1 bg-muted rounded h-4">
                  <div
                    className="bg-blue-500 h-4 rounded"
                    style={{ width: `${(count / summary.total) * 100}%` }}
                  />
                </div>
                <span className="w-12 text-right tabular-nums">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const VARIANT_CLASSES: Record<string, string> = {
  default: "bg-muted",
  warning: "bg-yellow-500/10 border border-yellow-500/30",
  danger: "bg-red-500/10 border border-red-500/30",
};

function StatCard({
  label,
  value,
  variant = "default",
}: {
  label: string;
  value: number;
  variant?: "default" | "warning" | "danger";
}) {
  return (
    <div className={`rounded-lg p-4 ${VARIANT_CLASSES[variant] ?? VARIANT_CLASSES.default}`}>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}
